import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { SessionService } from './session.service';
import { LocaleService } from './locale.service';
import * as _ from 'lodash';
import { StorageService } from './storage.service';
import { ConfigService } from './config.service';
import { User } from '../models/user.model';
import { distinctUntilChanged, filter, map, mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LocaleGraphqlService } from './locale-graphql.service';
import { DateFormatService } from './dateFormat.service';
import { WindowRefService } from './window.service';

/**
 * This service holds to observables (language$, communicationLanguage$) that provides the current language.
 */
@Injectable({
	providedIn: 'root'
})
export class LanguageService {
	/**
	 * Stream of the current language.
	 * Use this observable for all instant translations.
	 */
	language$: Observable<string>;
	/**
	 * Behavior subject that holds the stream internally
	 *
	 * @private
	 */
	private languageSubject$: BehaviorSubject<string>;

	/**
	 * Stream of the current language given to the API.
	 * Use this observable to request translated data from the API.
	 */
	communicationLanguage$: Observable<string>;
	/**
	 * Behavior subject that holds the stream internally
	 *
	 * @private
	 */
	private communicationLanguageSubject$: BehaviorSubject<string>;

	/**
	 * Current Language
	 *
	 * @deprecated The property should not be used
	 */
	language: string;

	/**
	 * Language selected by the user
	 */
	private languageByUser$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	/**
	 * Set to save the language stored in the local storage
	 *
	 * @param language
	 */
	private set storedLanguage(language: string) {
		if (language !== null) {
			if (language !== this.storedLanguage) {
				this.storageService.setItem('languageCode', language);
			}
		} else {
			this.storageService.removeItem('languageCode');
		}
	}

	/**
	 * Getter to load the language stored in the local storage
	 */
	private get storedLanguage(): string {
		return this.storageService.getItem('languageCode');
	}

	constructor(
		private sessionService: SessionService,
		private localeService: LocaleService,
		private storageService: StorageService,
		private configService: ConfigService,
		private translateService: TranslateService,
		private languageGraphqlService: LocaleGraphqlService,
		private dateFormatService: DateFormatService,
		private windowService: WindowRefService
	) {
		this.translateService.setDefaultLang('resources');

		const initialLanguage$ = this.getInititalLanguage().pipe(distinctUntilChanged());
		this.languageSubject$ = new BehaviorSubject(null);
		// Pipeline to determine the current language
		combineLatest([initialLanguage$, this.languageByUser$])
			.pipe(
				map(([initialLanguage, languageByUser]) => {
					// As long as the user does not select a language manually use the initial language
					if (languageByUser) {
						return languageByUser;
					}
					return initialLanguage;
				})
			)
			.pipe(
				mergeMap((language) => {
					// Switch translation to the selected language and make sure they are loaded
					// translateService.use does not emit any value
					// So we are waiting for the observable to emit and pass the through language
					return this.translateService.use(language).pipe(map(() => language));
				})
			)
			.pipe(
				map((language) => {
					this.language = language;
					// Set language in moment
					this.dateFormatService.setLocale(language);
					// Store the user's language selection if there is a user signed in
					if (sessionService.currentUser) {
						this.storedLanguage = language;
					}
					return language;
				})
			)
			.subscribe(this.languageSubject$);
		// To provide the language as stream that can be used from the outside
		// we need to map the observable from above to a BehaviorSubject
		// Because we don't want the BehaviorSubject to accessible from the outside
		// we then map the subject back to an observable
		this.language$ = this.languageSubject$.asObservable();

		this.communicationLanguageSubject$ = new BehaviorSubject(null);
		// Pipeline to determine which language was last communicated to the server
		// Every time the language has changed we inform the server about it
		// This way we can be sure to request further data in the correct language
		this.language$
			.pipe(
				filter((language) => {
					// prevent emit if we have no user, no language or the language has not changed
					return language != null && this.communicationLanguageSubject$.getValue() !== language && this.sessionService.currentUser != null;
				})
			)
			.pipe(
				mergeMap((language) => {
					// Set server side translation settings before the language$ observable emits the new value
					// On some pages, whenever the language changes, the required data is requested from the API.
					// To ensure that this data is returned in the correct language, the API must first be told
					// in which language the data should be returned.
					return this.languageGraphqlService.setCommunicationLanguage(language).pipe(map(() => language));
				})
			)
			.subscribe(this.communicationLanguageSubject$);

		// To provide the language as stream that can be used from the outside
		// we need to map the observable from above to a BehaviorSubject.
		// Because we don't want the BehaviorSubject to accessible from the outside
		// we then map the subject back to an observable
		this.communicationLanguage$ = this.communicationLanguageSubject$.asObservable();

		// Remove language from storage if the session was dropped
		this.sessionService.sessionObservable.subscribe((session) => {
			if (!session) {
				this.storedLanguage = null;
			}
		});
	}

	/**
	 * Change the current language selection
	 *
	 * @param language
	 */
	changeLanguage(language: string) {
		// trigger the subscriptions to update the current language
		this.languageByUser$.next(this.getSuitableLanguage(language));
	}

	/**
	 * Determine which language should be used on startup in depending on the current user
	 */
	private getInititalLanguage(): Observable<string> {
		return this.sessionService.currentUserObservable.pipe(
			map((user: User) => {
				let initialLanguage: string;

				// 1. Language from local storage when a user is signed in
				if (user && this.storedLanguage) {
					initialLanguage = this.storedLanguage;
				}

				if (user && !initialLanguage) {
					// 2. Language of the user itself
					if (user.language) {
						initialLanguage = user.language;
						// 3. Language of the company
					} else if (user.company?.languageCode) {
						initialLanguage = user.company?.languageCode;
					}
				}

				if (!initialLanguage) {
					// 4. Browser language
					const browserLanguage = this.getFirstBrowserLanguage();
					if (browserLanguage) {
						initialLanguage = browserLanguage;
					}
				}

				initialLanguage = this.getSuitableLanguage(initialLanguage);

				return initialLanguage;
			})
		);
	}

	/**
	 * Check whether the given language is available by checking tt against all configured languages
	 * and return the configured default language if not
	 *
	 * @param desiredLanguage
	 */
	private getSuitableLanguage(desiredLanguage: string): string {
		if (!desiredLanguage) {
			return this.configService.config.defaultLanguage;
		}

		// Init
		let suitableLanguage: string;

		// Available languages
		const availableLanguages = this.configService.config.languages;

		// 1. Find an exact match (be case insensitive and do not differ between hyphen and underscore)
		_.each(_.keys(availableLanguages), (languageKey) => {
			const lang = languageKey.toLowerCase().replace('_', '-');
			const desiredLang = desiredLanguage.toLowerCase().replace('_', '-');
			if (lang === desiredLang) {
				suitableLanguage = languageKey;
				return false;
			}
		});

		// 2. Try to find a partial match (available language starts with desired language)
		if (!suitableLanguage) {
			_.each(_.keys(availableLanguages), (languageKey) => {
				const lang = languageKey.toLowerCase().replace('_', '-');
				const desiredLang = desiredLanguage.toLowerCase().replace('_', '-');
				if (_.startsWith(lang, desiredLang)) {
					suitableLanguage = languageKey;
					return false;
				}
			});
		}

		// 4. Try to find the first part of desired language
		if (!suitableLanguage) {
			_.each(_.keys(availableLanguages), (languageKey) => {
				const lang = languageKey.toLowerCase().replace('_', '-');
				const desiredLang = desiredLanguage.toLowerCase().replace('_', '-').split('-')[0];
				if (_.startsWith(lang, desiredLang)) {
					suitableLanguage = languageKey;
					return false;
				}
			});
		}

		// 5. Default language
		if (!suitableLanguage) {
			suitableLanguage = this.configService.config.defaultLanguage;
		}

		return suitableLanguage;
	}

	/**
	 * Detect browser language setting
	 *
	 * Inspired by http://angular-translate.github.io/
	 */
	public getFirstBrowserLanguage(): string {
		if (!this.windowService.nativeWindow) {
			return null;
		}

		// Init vars
		const nav: any = this.windowService.nativeWindow.navigator;
		const browserLanguagePropertyKeys: string[] = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'];
		let language: string;

		// First try: Support for HTML 5.1 "navigator.languages"
		if (Array.isArray(nav.languages)) {
			_.each(nav.languages, (lang) => {
				if (lang && lang.length) {
					language = lang;
					return false;
				}
			});

			// If found
			if (language) {
				return language;
			}
		}

		// Second try: Support for other well known properties in browsers
		_.each(browserLanguagePropertyKeys, (property) => {
			const lang = nav[property];
			if (lang && lang.length) {
				language = lang;
				return false;
			}
		});

		// If found
		if (language) {
			return language;
		}

		// No language found
		return null;
	}
}
